import { Modal, Form } from "react-bootstrap";
import { useForm   }    from "react-hook-form";
import { Button    }    from "./Controls/Button";
import { Input     }    from "./Controls/Input";
import { UsAddress }    from "./Data/ApiTypes";
import { Select } from "./Controls/Select";
import { LocationInput } from "./Controls/LocationInput";
import { timezones } from "./Data/Timezones";

interface AddressModalProps {
    address: UsAddress;
    entry:   string;
    show:    boolean;
    setShow: ( show:    boolean         ) => void;
    onSave:  ( address: AddressAndEntry ) => void;
    timezone?: string;
}

export interface AddressAndEntry extends UsAddress {
    entry: string;
    timezone: string;
}

export function AddressModal( { address, entry, show, onSave, setShow, timezone }: AddressModalProps ) {
    const { register, setValue, watch, formState: { errors }, handleSubmit, control } = useForm<AddressAndEntry>(
        { defaultValues: { ...address, entry: entry, timezone: timezone || "" } } );

    function submit( data: AddressAndEntry ) {        
        onSave( data );
        setShow( false );
    }

    async function handlePlaceSelect(place: google.maps.places.Place | null) {
    if (!place) return;

    // Fetch all required fields (if not already fetched)
    await place.fetchFields({
        fields: [
        "formattedAddress",
        "addressComponents",
        "location",
        "viewport",
        "displayName"
        ]
    });

    if (place.addressComponents?.length) {
        const streetNumber = place.addressComponents.find(component =>
        component.types.includes('street_number')
        );
        const route = place.addressComponents.find(component =>
        component.types.includes('route')
        );

        let addressLine1 = '';
        if (streetNumber) {
        addressLine1 += streetNumber.shortText;
        }
        if (route) {
        addressLine1 += (addressLine1 ? ' ' : '') + route.longText;
        }

        if (!addressLine1 && place.addressComponents.length > 0) {
        addressLine1 = place.addressComponents[0].shortText!;
        }

        if (addressLine1) {
        setValue("addressLine1", addressLine1);
        }

        const cityComponent = place.addressComponents.find(component =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_3')
        );
        if (cityComponent) {
        setValue("city", cityComponent.longText!);
        }

        const stateComponent = place.addressComponents.find(component =>
        component.types.includes('administrative_area_level_1')
        );
        if (stateComponent) {
        setValue("state", stateComponent.shortText!);
        }

        const zipComponent = place.addressComponents.find(component =>
        component.types.includes('postal_code')
        );
        if (zipComponent) {
        setValue("zipCode", zipComponent.shortText!);
        }
    } else if (place.formattedAddress) {
        setValue("addressLine1", place.formattedAddress);
    }

    if (place.location) {
        setValue("latitude", parseFloat(place.location.lat().toFixed(6)));
        setValue("longitude", parseFloat(place.location.lng().toFixed(6)));
    }
    }

    return <Modal show={show} centered onEscapeKeyDown={ () => setShow( false ) }>
        <Form onSubmit={handleSubmit( submit )}>
            <Modal.Header className="fs-5">
                Edit Address and Entry
            </Modal.Header>
            <Modal.Body>
                <LocationInput
                name="addressLine1"
                control={control}
                rules={{ required: "Street address is required" }}
                onPlaceSelect={handlePlaceSelect}
                />    

                <Input
                    type="text"
                    {...register( "city", { required: "City is required" } )}
                    label="City"
                    error={errors.city} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }} className="gap-2">
                    <Input
                        type="text"
                        {...register( "state", { required: "State is required", maxLength: 2 } )}
                        label="State"
                        maxLength={2}
                        error={errors.state} />
                    <Input
                        type="text"
                        {...register( "zipCode", { required: "Zip code is required", maxLength: 10 } )}
                        label="Zip Code"
                        maxLength={10}
                        error={errors.zipCode} />
                </div>
                <Input
                    rows={3}
                    className="pt-0"
                    type="text"
                    as="textarea"
                    {...register( "entry", { required: "Entry instructions are required" } )}                    
                    label="Entry Instructions"
                    error={errors.entry} />
                            
                <Select label="Timezone"
                {...register( "timezone", { required: true } )}
                        error={undefined}>  
                        {timezones.map(tz => (
                            <option key={tz.value} value={tz.value}>
                            {tz.label}
                            </option>
                            ))}
                </Select>
            </Modal.Body>
            <Modal.Footer className="d-flex">
                <Button className="flex-grow-1" onClick={() => setShow( false )}>Cancel</Button>
                <Button className="flex-grow-1 btn-main" type="submit">Save</Button>
            </Modal.Footer>
        </Form>
    </Modal>;
}